import type { LocaleConfig } from "./config";

export type LocaleDecision =
  | { type: "passthrough" }
  | { type: "redirect"; location: string }
  | { type: "rewrite"; pathname: string; locale: string };

export interface ResolveLocaleRouteOptions {
  /** On preview deploys every localizable route is browsable in every known locale, for QA. */
  allowAllLocalizedRoutes?: boolean;
  /** Query string (including leading "?") to preserve across a redirect. */
  search?: string;
  config: LocaleConfig;
  /** Whether this app serves the given (unprefixed) route under its `[locale]` segment. */
  isLocalizableRoute: (route: string) => boolean;
  /** Whether the given route's translations have shipped in that one locale. */
  isLaunchedLocalizedRoute: (route: string, locale: string) => boolean;
}

// Paths ending in a file extension (e.g. /robots.txt, /sitemap.xml) are route
// handlers or static assets, not page routes — they must never be rewritten
// under [locale]/.
const FILE_EXT_RE = /\.[^/]+$/;

/**
 * Splits a request path into its leading locale prefix (if any) and the
 * *logical* path — the unprefixed route the app reasons about. Middleware that
 * matches on routes (password protection, experiment gating) must match the
 * logical path, or a locale-prefixed URL slips past it.
 */
export function stripLocalePrefix(
  pathname: string,
  config: LocaleConfig,
): { prefix: string | null; logicalPath: string } {
  const segments = pathname.split("/").filter(Boolean);
  const first = segments[0];
  // Recognise non-source locale prefixes AND the explicit default prefix (e.g.
  // "/en/...") so those URLs are caught and redirected rather than double-prefixed.
  if (
    first &&
    (config.KNOWN_PREFIXES.includes(first) || first === config.DEFAULT_PREFIX)
  ) {
    const rest = "/" + segments.slice(1).join("/");
    return { prefix: first, logicalPath: rest === "/" ? "/" : rest };
  }
  return { prefix: null, logicalPath: pathname };
}

/**
 * Decides how the locale-routing middleware should handle a request path:
 * pass it through untouched, redirect it to a canonical/logical URL, or
 * rewrite it internally to the `[locale]`-prefixed route Next.js should render.
 */
export function resolveLocaleRoute(
  pathname: string,
  options: ResolveLocaleRouteOptions,
): LocaleDecision {
  const {
    config,
    isLocalizableRoute,
    isLaunchedLocalizedRoute,
    allowAllLocalizedRoutes,
    search = "",
  } = options;

  if (FILE_EXT_RE.test(pathname)) return { type: "passthrough" };

  const { prefix, logicalPath: strippedPath } = stripLocalePrefix(
    pathname,
    config,
  );
  const logicalPath = strippedPath === "" ? "/" : strippedPath;

  if (!prefix) {
    // No locale prefix → rewrite to the default-locale path internally, but
    // only for routes this app actually serves under [locale]/. Unknown paths
    // pass through so they can be handled by their own route handlers or 404
    // without being silently rewritten to a different segment.
    if (!isLocalizableRoute(logicalPath)) return { type: "passthrough" };
    const rewritePathname =
      logicalPath === "/"
        ? `/${config.DEFAULT_PREFIX}`
        : `/${config.DEFAULT_PREFIX}${logicalPath}`;
    return {
      type: "rewrite",
      pathname: rewritePathname,
      locale: config.DEFAULT_PREFIX,
    };
  }

  // "/en/…" → 301 to the canonical unprefixed form; the default locale carries no URL prefix.
  if (prefix === config.DEFAULT_PREFIX) {
    return { type: "redirect", location: logicalPath + search };
  }

  if (prefix === config.PSEUDO_PREFIX) {
    // The pseudo locale has no "launched routes" list of its own — on preview
    // it is rewritable on any route this app serves under [locale]; in
    // production it must always redirect away, since it's a QA-only artifact.
    if (allowAllLocalizedRoutes && isLocalizableRoute(logicalPath)) {
      const rewritePathname =
        logicalPath === "/" ? `/${prefix}` : `/${prefix}${logicalPath}`;
      return { type: "rewrite", pathname: rewritePathname, locale: prefix };
    }
    return { type: "redirect", location: logicalPath + search };
  }

  // A prefixed URL for a route this app does not serve under [locale] must not
  // be rewritten into the segment — it would 404 there. Redirect to the
  // canonical unprefixed path so the route's own handler (or a normal 404)
  // takes over. This guard matters most on preview, where the launched-route
  // gate below is skipped and would otherwise rewrite any path.
  if (!isLocalizableRoute(logicalPath)) {
    return { type: "redirect", location: logicalPath + search };
  }

  // On preview deploys, every localizable page is browsable in every locale (so
  // QA can navigate the whole site); production still gates on launched routes.
  if (!allowAllLocalizedRoutes) {
    const locale = config.PREFIX_TO_LOCALE[prefix];
    if (!locale || !isLaunchedLocalizedRoute(logicalPath, locale)) {
      return { type: "redirect", location: logicalPath + search };
    }
  }

  const rewritePathname =
    logicalPath === "/" ? `/${prefix}` : `/${prefix}${logicalPath}`;
  return { type: "rewrite", pathname: rewritePathname, locale: prefix };
}
